import React from 'react';
import { useTranslation } from 'react-i18next';

import { CreateConnectionConfigurationForm } from '@/console/features/devices/components/create-device/create-connection-configuration-form';
import { CreateDeviceForm } from '@/console/features/devices/components/create-device/create-device-form';
import {
  TCreateDeviceFormProps,
  TCreateDeviceSchema,
} from '@/console/features/devices/components/create-device/create-device.types';
import {
  connectionConfigurationInputSchema,
  createDeviceInputSchema,
} from '@/console/features/devices/components/create-device/create-device.validation';
import { useCreateDevice } from '@/console/features/devices/hooks/use-create-device/use-create-device';
import { FormWizardProvider } from '@/shared/components/ui/form-wizard/context/form-wizard-context';
import { FormWizard } from '@/shared/components/ui/form-wizard/form-wizard';
import { useGroupScopedCreate } from '@/shared/features/groups/hooks/use-group-scoped-create/use-group-scoped-create';
import {
  FtpProtocolEnum,
  SmbProtocolEnum,
} from '@/shared/types/types.generated';

export const CreateDeviceContent: React.FC<TCreateDeviceFormProps> = ({
  handleClose,
  groupId,
}) => {
  const { t } = useTranslation();
  const groupScope = useGroupScopedCreate({
    action: 'device:createDevice',
    groupId,
  });
  const { devicePort, handleDeviceConfiguration, handleSubmit } =
    useCreateDevice(groupScope.groupId, handleClose);

  const deviceStep = {
    form: (
      <CreateDeviceForm
        groupId={groupScope.groupId}
        groupSelectProps={groupScope.groupSelectProps}
        isGroupRequired={groupScope.requiresGroup}
      />
    ),
    title: t('device:general_information'),
    props: {
      onSubmit: handleDeviceConfiguration,
      schema: createDeviceInputSchema(groupScope.requiresGroup),
      defaultValues: {
        groupId: groupScope.groupId,
      },
    },
  };

  const connectionConfigurationStep = {
    form: <CreateConnectionConfigurationForm />,
    title: t('device:network_information'),
    props: {
      schema: connectionConfigurationInputSchema,
      defaultValues: {
        connectionConfiguration: {
          ipAddress: '',
          port: devicePort,
        },
        ftpConfiguration: {
          port: 22,
          rootDirectory: '/',
          protocol: FtpProtocolEnum.Sftp,
        },
        smbConfiguration: {
          port: 445,
          rootDirectory: '/',
          protocol: SmbProtocolEnum.Smb,
        },
      },
    },
  };

  return (
    <FormWizard<TCreateDeviceSchema>
      forms={[deviceStep, connectionConfigurationStep]}
      handleClose={handleClose}
      onFormSubmit={handleSubmit}
    />
  );
};

export const CreateDevice: React.FC<TCreateDeviceFormProps> = ({
  handleClose,
  groupId,
}) => {
  return (
    <FormWizardProvider>
      <CreateDeviceContent groupId={groupId} handleClose={handleClose} />
    </FormWizardProvider>
  );
};
